// Live's "Generate captions" and "Regenerate captions" (#239 part 2, spec
// docs/specs/editor/captions.md): one `caption.transcribe` job a file the timeline's videos and
// sounds play, each read as a job's page reads one (stream events, then the fallback timer while
// the stream is down), and each succeeded job's JSON output read from the media CDN and checked
// against the contract. Once every job has ended, the words are placed by the clips as they are
// then, cut by the project's paragraph settings and position, and replace every caption: one undo
// step, no question and no cost, live's. The editor holds one run for the media panel's button and
// the assistant's Auto Captions (#242), so a run lands after the Captions tab is left, and each
// asks for its line: the run's last word, said when it lands. The assistant's run takes live's
// assistant look and cut (`assistant-captions.ts`); the button's, the project's.
import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { fetchSignedFile } from "../../../api/download";
import { getGetJobQueryOptions, useStartJob } from "../../../api/generated/endpoints/generation/generation";
import type { CaptionTranscribeRequest, CaptionTranscript, Job } from "../../../api/generated/model";
import { CaptionTranscript as TranscriptSchema } from "../../../api/generated/zod";
import { attemptKey } from "../../../platform/idempotency";
import { canChange, jobRefetchInterval } from "../../../platform/jobs/refetch";
import { useStreamStatus } from "../../../platform/stream-status";
import type { AspectRatio } from "../project";
import { editTimeline, newId, type Timeline, type TimelineEdit } from "../timeline";
import { assistantCaptions, assistantSettings } from "./assistant-captions";
import { cutCaptions } from "./recut";
import {
  CAPTIONS_BUSY,
  noCaptionRun,
  noCaptionTrack,
  spokenClips,
  transcribedSources,
  wordsOnVideo,
} from "./transcribe";
import type { Said } from "./Transcript";
import { MAX_CAPTIONS, MAX_TIMELINE_CAPTION_WORDS, type TimedWord } from "./words";

export interface GenerateCaptionsOptions {
  teamId: string;
  brandId: string;
  timeline: Timeline;
  aspectRatio: AspectRatio;
  onEdit: (edit: TimelineEdit) => void;
  onSay: (said: Said | null) => void;
}

/** A run's end: its line, and the words it placed on the video's time (none when it placed none). */
export interface Landed {
  said: Said;
  words: readonly TimedWord[];
  /**
   * The editor's timeline with the run's captions on it, as they landed (null when it placed
   * none): edits made while it ran included, so a rough cut after it is cut on it.
   */
  timeline: Timeline | null;
}

/** A run's end that placed nothing: its line alone. */
function only(said: Said): Landed {
  return { said, words: [], timeline: null };
}

/** A run: each file's job, or null where its start was refused. */
/** Whose run it is: the button's (the project's look and cut) or the assistant's (live's). */
export type CaptionLook = "project" | "assistant";

interface Run {
  id: number;
  look: CaptionLook;
  jobs: readonly { key: string; jobId: string | null }[];
}

/** What a run's jobs gave: each read transcript by its file, and how many jobs gave none. */
interface Outcome {
  look: CaptionLook;
  transcripts: ReadonlyMap<string, CaptionTranscript>;
  failed: number;
  total: number;
}

/** Live's words for a run's end, by how many captions it made. */
function generated(count: number): string {
  return count === 1 ? "Generated 1 caption segment!" : `Generated ${count} caption segments!`;
}

/** A transcript file read and checked against the contract. */
async function readTranscript(url: string, signal: AbortSignal): Promise<CaptionTranscript> {
  const file = await fetchSignedFile(url, signal);
  return TranscriptSchema.parse(JSON.parse(await file.text())) as CaptionTranscript;
}

/** The run's outcome once every job has ended and every output is read, else null. */
function outcomeOf(
  run: Run,
  jobs: readonly UseQueryResult<Job>[],
  outputs: readonly UseQueryResult<CaptionTranscript>[],
): Outcome | null {
  const transcripts = new Map<string, CaptionTranscript>();
  let failed = 0;
  let at = 0;
  for (const { key, jobId } of run.jobs) {
    if (jobId === null) {
      failed += 1;
      continue;
    }
    const job = jobs[at];
    const output = outputs[at];
    at += 1;
    if (job === undefined || output === undefined) return null;
    if (job.isError) {
      failed += 1;
      continue;
    }
    if (canChange(job.data?.status)) return null;
    if (job.data?.status !== "succeeded" || output.isError) failed += 1;
    else if (output.isSuccess) transcripts.set(key, output.data);
    else return null;
  }
  return { look: run.look, transcripts, failed, total: run.jobs.length };
}

/**
 * A run's words placed by the clips as they are now, cut, and put on the timeline: its line, the
 * words placed and the timeline they landed on (for a rough cut after it, spec assistant-rough-cut).
 */
async function place(ended: Outcome, editor: GenerateCaptionsOptions, look: CaptionLook): Promise<Landed> {
  const { timeline, aspectRatio } = editor;
  try {
    const words = wordsOnVideo(spokenClips(timeline), ended.transcripts);
    if (words.length === 0) {
      // Every job failed: said as a failure (spec decision 40; live says no speech was heard).
      return only(
        ended.failed === ended.total
          ? { tone: "error", text: "Failed to generate captions." }
          : { tone: "info", text: "No spoken words detected in the media clips." },
      );
    }
    const refused = noCaptionTrack(timeline);
    if (refused !== null) return only(refused);
    const segments =
      look === "assistant"
        ? assistantCaptions(words, aspectRatio).map((each) => ({ ...each, id: newId() }))
        : await cutCaptions(words, timeline.captions, aspectRatio);
    const total = segments.reduce((sum, segment) => sum + segment.words.length, 0);
    if (segments.length > MAX_CAPTIONS || total > MAX_TIMELINE_CAPTION_WORDS) {
      return only({
        tone: "error",
        text: `The speech holds more than a video's captions can (${MAX_CAPTIONS} captions, ${MAX_TIMELINE_CAPTION_WORDS} words).`,
      });
    }
    if (segments.length === 0) {
      return only({ tone: "info", text: "No caption segments were produced from the audio." });
    }
    const newTrackId = newId();
    const replace: TimelineEdit = { type: "replaceCaptions", captions: segments, newTrackId };
    // The assistant's look comes with its captions, one undo step (live's skill's clips carry it).
    const styled: TimelineEdit = {
      type: "setCaptions",
      captions: assistantSettings(timeline.captions),
      cut: null,
      idPrefix: "",
      newTrackId,
    };
    const placed: TimelineEdit = look === "assistant" ? { type: "many", edits: [styled, replace] } : replace;
    editor.onEdit(placed);
    return { said: { tone: "success", text: generated(segments.length) }, words, timeline: editTimeline(timeline, placed) };
  } catch {
    return only({ tone: "error", text: "Failed to generate captions." });
  }
}

export function useGenerateCaptions(options: GenerateCaptionsOptions) {
  const { teamId } = options;
  const stream = useStreamStatus();
  const start = useStartJob();
  const [starting, setStarting] = useState(false);
  const [run, setRun] = useState<Run | null>(null);
  const [placing, setPlacing] = useState(false);
  // The editor as it is now, for a run that ends later: its clips, look, edit and line.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  const runs = useRef(0);
  const delivered = useRef(0);
  // Who waits for each run's end, by its id.
  const waiting = useRef(new Map<number, (landed: Landed) => void>());

  const asked = run?.jobs.flatMap(({ jobId }) => (jobId === null ? [] : [jobId])) ?? [];
  const jobs = useQueries({
    queries: asked.map((jobId) => ({
      ...getGetJobQueryOptions(teamId, jobId),
      refetchInterval: (query: { state: { data: Job | undefined } }) =>
        jobRefetchInterval(query.state.data?.status, stream),
    })),
  });
  const urls = jobs.map((job) => (job.data?.status === "succeeded" ? (job.data.outputs[0]?.url ?? null) : null));
  const outputs = useQueries({
    queries: asked.map((jobId, at) => ({
      queryKey: ["caption-transcript", jobId],
      enabled: urls[at] !== null && urls[at] !== undefined,
      queryFn: ({ signal }: { signal: AbortSignal }) => readTranscript(urls[at]!, signal),
      staleTime: Infinity,
      retry: false,
    })),
  });
  const outcome = run === null ? null : outcomeOf(run, jobs, outputs);
  // The outcome as of the last render, for the effect that hands it over once.
  const outcomeNow = useRef(outcome);
  useEffect(() => {
    outcomeNow.current = outcome;
  });

  // A run ends once: its words land through the editor as it is then.
  const ended = run !== null && outcome !== null ? run.id : null;
  useEffect(() => {
    const result = outcomeNow.current;
    if (ended === null || result === null || delivered.current === ended) return;
    delivered.current = ended;
    setRun(null);
    setPlacing(true);
    void place(result, latest.current, result.look)
      .then((landed) => {
        latest.current.onSay(landed.said);
        waiting.current.get(ended)?.(landed);
      })
      .finally(() => {
        waiting.current.delete(ended);
        setPlacing(false);
      });
  }, [ended]);

  const busy = starting || run !== null || placing;
  return {
    busy,
    /**
     * Live's Generate and Regenerate, or the assistant's Auto Captions (`look: "assistant"`) on
     * the timeline its plan made (`from`): refused, said, with no video or sound or no track
     * room. Its line and the words it placed, once the run has landed.
     */
    generate: async (from?: Timeline, look: CaptionLook = "project"): Promise<Landed> => {
      const { teamId: team, brandId, onSay } = latest.current;
      const timeline = from ?? latest.current.timeline;
      if (busy) return only(CAPTIONS_BUSY);
      onSay(null);
      const refused = noCaptionRun(timeline);
      if (refused !== null) {
        onSay(refused);
        return only(refused);
      }
      const id = (runs.current += 1);
      const landed = new Promise<Landed>((resolve) => waiting.current.set(id, resolve));
      const files = transcribedSources(spokenClips(timeline));
      setStarting(true);
      // Each file's start on its own: a refused one gives no words, live's per-clip failure.
      const started = await Promise.allSettled(
        files.map((file) => {
          const data: CaptionTranscribeRequest = { type: "caption.transcribe", source: file.source };
          return start.mutateAsync({ teamId: team, brandId, data, headers: { "Idempotency-Key": attemptKey(data, undefined) } });
        }),
      );
      setStarting(false);
      setRun({
        id,
        look,
        jobs: files.map((file, at) => {
          const result = started[at];
          return { key: file.key, jobId: result?.status === "fulfilled" ? result.value.id : null };
        }),
      });
      return landed;
    },
  };
}

export type GenerateCaptions = ReturnType<typeof useGenerateCaptions>;
